import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { IMAGES, FURNITURE_PILLARS } from "@/data/content";

const Furniture = () => (
    <div data-testid="furniture-page">
        <Seo
            title="Bespoke Furniture & Manufacturing | Kavraj Spaces — New Delhi"
            description="Bespoke residential furniture, in-house manufacturing and project supply for homes, offices, franchises, hospitals and institutions — crafted by Kavraj Spaces, New Delhi."
        />

        <PageHero
            eyebrow="Capability 03 — Furniture"
            title="Furniture made for the space it lives in."
            intro="An in-house manufacturing division crafting bespoke residential furniture and supplying projects at scale — for homes, offices, corporates, brand franchises, restaurants, hospitals and institutions."
            testid="furniture-hero"
        />

        <section className="bg-cream" data-testid="furniture-craft">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-20 sm:px-12 lg:grid-cols-2 lg:gap-20 lg:px-20 lg:py-32">
                <Reveal>
                    <img
                        src={IMAGES.furniture.url}
                        alt={IMAGES.furniture.alt}
                        loading="lazy"
                        className="h-full w-full object-cover"
                    />
                </Reveal>
                <Reveal delay={120}>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                        Made in-house
                    </p>
                    <h2 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                        Our own workshop, our own word on quality.
                    </h2>
                    <p className="mt-6 text-base leading-relaxed text-stone2">
                        Owning the manufacturing changes everything: timber is selected by us,
                        joinery is cut by us, and finishes are signed off by us. Nothing about the
                        quality of a Kavraj Spaces piece is outsourced.
                    </p>
                    <p className="mt-5 text-base leading-relaxed text-stone2">
                        The same capability serves two worlds — one-off bespoke pieces for private
                        homes, and volume supply for projects that need hundreds of identical,
                        dependable units delivered on schedule.
                    </p>
                </Reveal>
            </div>
        </section>

        <section className="border-y border-bronze/15 bg-cream-muted" data-testid="furniture-pillars">
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-32">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                        What the division does
                    </p>
                    <h2 className="mt-4 max-w-2xl font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Three ways we furnish a space.
                    </h2>
                </Reveal>
                <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
                    {FURNITURE_PILLARS.map((pillar, i) => (
                        <Reveal key={pillar.title} delay={i * 120}>
                            <div
                                className="h-full border-t-2 border-bronze bg-cream p-8"
                                data-testid={`furniture-pillar-${i}`}
                            >
                                <span className="font-serif text-sm italic text-bronze">0{i + 1}</span>
                                <h3 className="mt-3 font-serif text-2xl leading-snug text-ink">
                                    {pillar.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-stone2">{pillar.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={200}>
                    <p className="mt-12 max-w-2xl text-sm leading-relaxed text-stone2">
                        Project supply experience spans homes, offices, franchises, hospitals and
                        institutions — including furniture delivered across 100+ franchise locations.
                    </p>
                </Reveal>
            </div>
        </section>

        <CtaBand
            title="A piece, a room, or an entire project."
            text="Share the space and the standard you have in mind — we will propose how to furnish it."
        />
    </div>
);

export default Furniture;
